import {
  IsOneOfInStringListFold,
  Not,
  NumberEquals,
  NumberGreaterThan,
  StringContainsFold,
  StringEquals,
  StringListContains,
  StringListContainsAll,
} from '@vendasta/automata';
import { ConversionPointsOptionsDisplayService } from './dynamic-options/conversion-point-options/conversion-point-options-display.service';
import { ConversionPointOptionsComponent } from './dynamic-options/conversion-point-options/conversion-point-options.component';
import { DynamicStringDisplayService } from './dynamic-options/dynamic-string.service';
import { EmptyStringComponent } from './dynamic-options/empty-string-component';
import { FreeTextInputComponent } from './dynamic-options/free-text-input/free-text-input.component';
import { FreeTextListInputComponent } from './dynamic-options/free-text-list-input/free-text-list-input.component';
import { PositiveIntegerDisplayService } from './dynamic-options/positive-integer/positive-integer-display.service';
import { PositiveIntegerComponent } from './dynamic-options/positive-integer/positive-integer.component';
import { RevenueDisplayService } from './dynamic-options/revenue-options/revenue-display.service';
import { RevenueOptionsComponent } from './dynamic-options/revenue-options/revenue-options.component';
import { DataFilter } from './interface';

const PARTNER_SNAPSHOT_COUNT = '{.internal_account_data.partner_stats.generated_snapshots}';
class PartnerGeneratedSnapshotsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_SNAPSHOT_COUNT)),
    new NumberEquals(PARTNER_SNAPSHOT_COUNT),
    new NumberGreaterThan(PARTNER_SNAPSHOT_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.SNAPSHOTS_CREATED';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_DAYS_ACTIVE_IN_PLATFORM = '{.internal_account_data.partner_stats.days_active_in_platform}';
class PartnerDaysActiveInPlatformDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_DAYS_ACTIVE_IN_PLATFORM)),
    new NumberEquals(PARTNER_DAYS_ACTIVE_IN_PLATFORM),
    new NumberGreaterThan(PARTNER_DAYS_ACTIVE_IN_PLATFORM),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.DAYS_ACTIVE_IN_PLATFORM';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_ACTIVE_PAID_PRODUCTS_COUNT = '{.internal_account_data.partner_stats.active_paid_products}';
class PartnerActivePaidProductsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_ACTIVE_PAID_PRODUCTS_COUNT)),
    new NumberEquals(PARTNER_ACTIVE_PAID_PRODUCTS_COUNT),
    new NumberGreaterThan(PARTNER_ACTIVE_PAID_PRODUCTS_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.ACTIVE_PRODUCTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_ACCOUNTS_COUNT = '{.internal_account_data.partner_stats.accounts}';
class PartnerAccountsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_ACCOUNTS_COUNT)),
    new NumberEquals(PARTNER_ACCOUNTS_COUNT),
    new NumberGreaterThan(PARTNER_ACCOUNTS_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.ACCOUNTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_OPEN_OPPORTUNITIES_COUNT = '{.internal_account_data.partner_stats.open_opportunities}';
class PartnerOpenOpportunitiesCountAccountsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_OPEN_OPPORTUNITIES_COUNT)),
    new NumberEquals(PARTNER_OPEN_OPPORTUNITIES_COUNT),
    new NumberGreaterThan(PARTNER_OPEN_OPPORTUNITIES_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.OPEN_OPPORTUNITIES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

// Use conversion point as internal filter until field is properly exposed to frontend for all partners.
const PARTNER_CONVERSION_POINT = '{.internal_account_data.partner_stats.conversion_point}';
class ConversionPointDataFilter implements DataFilter {
  rules = [
    new StringListContains(PARTNER_CONVERSION_POINT),
    new StringListContainsAll(PARTNER_CONVERSION_POINT),
    new Not(new StringListContains(PARTNER_CONVERSION_POINT)),
    new Not(new StringListContainsAll(PARTNER_CONVERSION_POINT)),
  ];
  dataEntryComponent = ConversionPointOptionsComponent;
  displayService = ConversionPointsOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.CONVERSION_POINTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

// Use number of employees as internal filter until field is properly exposed to frontend for all partners.
const PARTNER_NUMBER_OF_EMPLOYEES = '{.internal_account_data.partner_stats.number_of_employees}';
class NumberOfEmployeesDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_NUMBER_OF_EMPLOYEES)),
    new NumberEquals(PARTNER_NUMBER_OF_EMPLOYEES),
    new NumberGreaterThan(PARTNER_NUMBER_OF_EMPLOYEES),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.NUMBER_OF_EMPLOYEES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_ESTIMATED_ANNUAL_REVENUE = '{.internal_account_data.partner_stats.estimated_annual_revenue}';
class RevenueFilter implements DataFilter {
  rules = [
    new StringListContains(PARTNER_ESTIMATED_ANNUAL_REVENUE),
    new Not(new StringListContains(PARTNER_ESTIMATED_ANNUAL_REVENUE)),
  ];
  dataEntryComponent = RevenueOptionsComponent;
  displayService = RevenueDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.ESTIMATED_ANNUAL_REVENUE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
  ];
}

const PARTNER_ACC_UTM_CAMPAIGN = '{.internal_account_data.utm_attributes.utm_campaign}';
class UTMCampaignFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(PARTNER_ACC_UTM_CAMPAIGN)),
    new StringEquals(PARTNER_ACC_UTM_CAMPAIGN),
    new StringContainsFold(PARTNER_ACC_UTM_CAMPAIGN),
    new Not(new StringContainsFold(PARTNER_ACC_UTM_CAMPAIGN)),
    new IsOneOfInStringListFold(PARTNER_ACC_UTM_CAMPAIGN),
    new Not(new IsOneOfInStringListFold(PARTNER_ACC_UTM_CAMPAIGN)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    FreeTextInputComponent,
    FreeTextInputComponent,
    FreeTextListInputComponent,
    FreeTextListInputComponent,
  ];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.UTM_CAMPAIGN';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_CONTAIN',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
  ];
}

const PARTNER_ACC_UTM_MEDIUM = '{.internal_account_data.utm_attributes.utm_medium}';
export class UTMMediumFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(PARTNER_ACC_UTM_MEDIUM)),
    new StringEquals(PARTNER_ACC_UTM_MEDIUM),
    new StringContainsFold(PARTNER_ACC_UTM_MEDIUM),
    new Not(new StringContainsFold(PARTNER_ACC_UTM_MEDIUM)),
    new IsOneOfInStringListFold(PARTNER_ACC_UTM_MEDIUM),
    new Not(new IsOneOfInStringListFold(PARTNER_ACC_UTM_MEDIUM)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    FreeTextInputComponent,
    FreeTextInputComponent,
    FreeTextListInputComponent,
    FreeTextListInputComponent,
  ];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.UTM_MEDIUM';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_CONTAIN',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
  ];
}

const PARTNER_ACC_UTM_SOURCE = '{.internal_account_data.utm_attributes.utm_source}';
export class UTMSourceFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(PARTNER_ACC_UTM_SOURCE)),
    new StringEquals(PARTNER_ACC_UTM_SOURCE),
    new StringContainsFold(PARTNER_ACC_UTM_SOURCE),
    new Not(new StringContainsFold(PARTNER_ACC_UTM_SOURCE)),
    new IsOneOfInStringListFold(PARTNER_ACC_UTM_SOURCE),
    new Not(new IsOneOfInStringListFold(PARTNER_ACC_UTM_SOURCE)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    FreeTextInputComponent,
    FreeTextInputComponent,
    FreeTextListInputComponent,
    FreeTextListInputComponent,
  ];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.UTM_SOURCE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_CONTAIN',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
  ];
}

const PARTNER_ACC_UTM_CONTENT = '{.internal_account_data.utm_attributes.utm_content}';
export class UTMContentFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(PARTNER_ACC_UTM_CONTENT)),
    new StringEquals(PARTNER_ACC_UTM_CONTENT),
    new StringContainsFold(PARTNER_ACC_UTM_CONTENT),
    new Not(new StringContainsFold(PARTNER_ACC_UTM_CONTENT)),
    new IsOneOfInStringListFold(PARTNER_ACC_UTM_CONTENT),
    new Not(new IsOneOfInStringListFold(PARTNER_ACC_UTM_CONTENT)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    FreeTextInputComponent,
    FreeTextInputComponent,
    FreeTextListInputComponent,
    FreeTextListInputComponent,
  ];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.UTM_CONTENT';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_CONTAIN',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
  ];
}

const PARTNER_ACC_UTM_TERM = '{.internal_account_data.utm_attributes.utm_term}';
export class UTMTermFilter implements DataFilter {
  rules = [
    new Not(new StringEquals(PARTNER_ACC_UTM_TERM)),
    new StringEquals(PARTNER_ACC_UTM_TERM),
    new StringContainsFold(PARTNER_ACC_UTM_TERM),
    new Not(new StringContainsFold(PARTNER_ACC_UTM_TERM)),
    new IsOneOfInStringListFold(PARTNER_ACC_UTM_TERM),
    new Not(new IsOneOfInStringListFold(PARTNER_ACC_UTM_TERM)),
  ];
  dataEntryComponentList = [
    EmptyStringComponent,
    EmptyStringComponent,
    FreeTextInputComponent,
    FreeTextInputComponent,
    FreeTextListInputComponent,
    FreeTextListInputComponent,
  ];
  displayService = DynamicStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.UTM_TERM';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EXISTS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_EXIST',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.DOES_NOT_CONTAIN',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
  ];
}

export const ACCOUNT_INTERNAL_DATA_COMPONENTS: Map<string, DataFilter> = new Map<string, DataFilter>([
  [PARTNER_SNAPSHOT_COUNT, new PartnerGeneratedSnapshotsDataFilter()],
  [PARTNER_DAYS_ACTIVE_IN_PLATFORM, new PartnerDaysActiveInPlatformDataFilter()],
  [PARTNER_ACTIVE_PAID_PRODUCTS_COUNT, new PartnerActivePaidProductsDataFilter()],
  [PARTNER_ACCOUNTS_COUNT, new PartnerAccountsDataFilter()],
  [PARTNER_OPEN_OPPORTUNITIES_COUNT, new PartnerOpenOpportunitiesCountAccountsDataFilter()],
  [PARTNER_CONVERSION_POINT, new ConversionPointDataFilter()],
  [PARTNER_NUMBER_OF_EMPLOYEES, new NumberOfEmployeesDataFilter()],
  [PARTNER_ESTIMATED_ANNUAL_REVENUE, new RevenueFilter()],
  [PARTNER_ACC_UTM_CAMPAIGN, new UTMCampaignFilter()],
  [PARTNER_ACC_UTM_MEDIUM, new UTMMediumFilter()],
  [PARTNER_ACC_UTM_SOURCE, new UTMSourceFilter()],
  [PARTNER_ACC_UTM_CONTENT, new UTMContentFilter()],
  [PARTNER_ACC_UTM_TERM, new UTMTermFilter()],
]);
